import { apiRequest } from '../../../utils/utils.js';
import { getApiName } from '../../convert-api-result-to-google-json/index.js';
import { forceUpdateInputValueWithoutOpeningDropdown } from '../../acf-input-address/elements/input.js';
// Popup helpers and constants for leaflet map
const POPUP_ADRESS_CLOSEST_CSS = 'leaflet-closest-address';
const POPUP_LOADING_TXT = 'Laden...';
const POPUP_HEADER_TXT = 'Wil je dit de nieuwe locatie maken?';
const POPUP_SUBTITLE_TXT = 'Dichtsbijzijnde adres:';
const POPUP_BTN_YES_TXT = 'Ja';
const POPUP_BTN_NO_TXT = 'Nee';

const API_BASE_URL =
	'https://api.pdok.nl/bzk/locatieserver/search/v3_1/reverse?';
const API_LAT_PARAM = 'lat=';
const API_LON_PARAM = 'lon=';
const API_ROWS_PARAM = 'rows=';

const API_LOOKUP_URL =
	'https://api.pdok.nl/bzk/locatieserver/search/v3_1/lookup?';
const API_ID_PARAM = 'id=';

function renderPopup( context ) {
	const { map, popup, latlng, address } = context;
	const html = /*html*/ `
		<h3 class='leaflet-popup-header'>${ POPUP_HEADER_TXT }</h3>
			<p>
				<span>${ POPUP_SUBTITLE_TXT }</span>
				<span class='${ POPUP_ADRESS_CLOSEST_CSS }' data-loading='true'>${ address }</span>
			</p>
			<div class='buttons-container'>
				<button type='button' class='popup-button popup-confirm'>${ POPUP_BTN_YES_TXT }</button>
				<button type='button' class='popup-button popup-cancel'>${ POPUP_BTN_NO_TXT }</button>
		</div>
	`;
	popup.setLatLng( latlng ).setContent( html ).openOn( map );
}

// Step 6 - Handle clicks on the map
export function onMapClick( context ) {
	const { popup, event, map, mapState, actionsAllowedPerMs, forceApiDelay } =
		context;
	const { lat: clickLat, lng: clickLng } = event.latlng;

	// Step 7 - create and show the popup
	renderPopup( {
		map,
		popup,
		latlng: event.latlng,
		address: POPUP_LOADING_TXT,
	} );

	// Step 8 - Add listeners to yes/no buttons
	addPopupButtonListeners( {
		...context,
		clickLat,
		clickLng,
		lastApiDoc: mapState.lastApiDoc,
		duringLoading: true,
	} );

	// Step 9 - set loading attrribute while waiting on data.
	const popupContentNode = popup._contentNode;
	const addressSpan = popupContentNode
		? popupContentNode.querySelector( `.${ POPUP_ADRESS_CLOSEST_CSS }` )
		: null;
	if ( addressSpan ) addressSpan.setAttribute( 'data-loading', 'true' );

	// Step 10 - Setup Logic for Rate limit / prevent click spam
	if ( mapState.addressFetchTimeoutId ) {
		clearTimeout( mapState.addressFetchTimeoutId );
		mapState.addressFetchTimeoutId = null;
	}

	const now = Date.now();
	const timeSinceLastFetch = now - mapState.lastAddressFetchTime;

	// Step 11 - Actually find the nearest adress
	const fetchAddress = () =>
		fetchAndHandleAddress( {
			...context,
			clickLat,
			clickLng,
		} );

	if ( timeSinceLastFetch >= actionsAllowedPerMs ) {
		fetchAddress();
	} else {
		mapState.addressFetchTimeoutId = setTimeout(
			fetchAddress,
			forceApiDelay
		);
	}
}

// Step 12 - Fetch and handle the address
export async function fetchAndHandleAddress( context ) {
	const { clickLat, clickLng, mapState } = context;
	const translatedAddress = getApiName( 'address', 'pdok' );
	mapState.lastAddressFetchTime = Date.now();

	const doc = await fetchClosestAddress( { clickLat, clickLng } );
	mapState.lastApiDoc = doc;

	const address = doc ? doc[ translatedAddress ] : 'Geen adres gevonden';

	// Step 13 - Update popup with adress AND add yes/no listeners
	await handleAddressFetch( {
		...context,
		address,
		lastApiDoc: mapState.lastApiDoc,
	} );
}

function updateAddressSpan( context ) {
	const { popup, address } = context;
	const addressSpanLatest = popup._contentNode
		? popup._contentNode.querySelector( `.${ POPUP_ADRESS_CLOSEST_CSS }` )
		: null;
	if ( addressSpanLatest ) {
		addressSpanLatest.textContent = address;
		addressSpanLatest.setAttribute( 'data-loading', 'false' );
	}
}

async function handleAddressFetch( context ) {
	// Step 13.1 - Set the address span content
	updateAddressSpan( { popup: context.popup, address: context.address } );

	// Step 14 - Add yes/no listeners
	addPopupButtonListeners( {
		...context,
		duringLoading: false,
	} );
}

async function fetchClosestAddress( context ) {
	const { clickLat, clickLng } = context;
	// Example PDOK Api Url:
	// https://api.pdok.nl/bzk/locatieserver/search/v3_1/reverse?lat=52.09325905948444&lon=5.119071006774903&rows=1
	const url = `${ API_BASE_URL }${ API_LAT_PARAM }${ clickLat }&${ API_LON_PARAM }${ clickLng }&${ API_ROWS_PARAM }1`;
	try {
		const data = await apiRequest( url );
		const doc =
			data.response && data.response.docs && data.response.docs[ 0 ]
				? data.response.docs[ 0 ]
				: null;
		return doc;
	} catch {
		return null;
	}
}

// Step 15 - Popup yes button click
export async function handleYesClick( context ) {
	const { event, marker, map, clickLat, clickLng, container, lastApiDoc } =
		context;

	// Step 16 - set new marker location, move map and close popup
	event.stopPropagation();
	marker.setLatLng( [ clickLat, clickLng ] );
	map.panTo( [ clickLat, clickLng ] );
	map.closePopup();

	// Step 17 - based on location ID, do another api request
	const input = container.querySelector( 'input[type=search]' );
	if ( input && lastApiDoc && lastApiDoc.id ) {
		// Example PDOK Api Url:
		// https://api.pdok.nl/bzk/locatieserver/search/v3_1/lookup?id=adr-ff7f997dc00b0472b88ebfcc860a4a22
		const lookupUrl = `${ API_LOOKUP_URL }${ API_ID_PARAM }${ lastApiDoc.id }`;
		const lookupResult = await apiRequest( lookupUrl );
		const lookupDoc =
			lookupResult.response &&
			lookupResult.response.docs &&
			lookupResult.response.docs[ 0 ]
				? lookupResult.response.docs[ 0 ]
				: null;
		if ( lookupDoc ) {
			// Step 18 - convert pdok data into backwards compatible google like json and update ACF hidden field
			forceUpdateInputValueWithoutOpeningDropdown( input, lookupDoc );
		}
	}
}

export function handleNoClick( context ) {
	const { event, map } = context;
	event.stopPropagation();
	map.closePopup();
}

// Step 14 - Add yes/no listeners
function addPopupButtonListeners( context ) {
	const { popup, map, duringLoading = false } = context;
	const popupContentNode = popup._contentNode;
	const yesBtn = popupContentNode
		? popupContentNode.querySelector( '.popup-confirm' )
		: null;
	const noBtn = popupContentNode
		? popupContentNode.querySelector( '.popup-cancel' )
		: null;

	if ( yesBtn ) yesBtn.replaceWith( yesBtn.cloneNode( true ) );
	if ( noBtn ) noBtn.replaceWith( noBtn.cloneNode( true ) );

	const freshYesBtn = popupContentNode.querySelector( '.popup-confirm' );
	const freshNoBtn = popupContentNode.querySelector( '.popup-cancel' );

	if ( duringLoading ) {
		freshNoBtn?.addEventListener( 'click', ( event ) =>
			//14.1 close popup
			handleNoClick( { event, map } )
		);
	} else {
		freshYesBtn?.addEventListener( 'click', ( event ) =>
			//14.2 close popup and update actual address
			handleYesClick( {
				...context,
				event,
			} )
		);
		freshNoBtn.addEventListener( 'click', ( event ) =>
			handleNoClick( { event, map } )
		);
	}
}
